import React, { useEffect, useState, useCallback } from "react";
import { Loader2, TrendingUp, UserPlus, Eye, Percent } from "lucide-react";
import { api, formatErr } from "../../lib/api";
import { toast } from "sonner";
import AdminErrorState from "./AdminErrorState";

/**
 * ConversionFunnelCard — daily anonymous → signup breakdown.
 *
 * Pairs unique-visitor counts from `presence_hourly` (populated in
 * `presence_ping`) with new-user rows from `db.users.created_at`. Backed
 * by `/api/admin/conversion/funnel?days=N`. Meant to be the operator's
 * scoreboard for the campsite-detail signup nudge, sticky save-bar and
 * soft feature-gates: if we're doing anything right, the rate goes up.
 */
const num = (v) => (typeof v === "number" ? v.toLocaleString("en-GB") : "—");

const humanDate = (iso) => {
  try {
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-GB", {
      weekday: "short", day: "numeric", month: "short",
    });
  } catch { return iso; }
};

export default function ConversionFunnelCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [days, setDays] = useState(14);

  const load = useCallback(async (d) => {
    setBusy(true);
    setErr(null);
    try {
      const { data } = await api.get("/admin/conversion/funnel", { params: { days: d } });
      setData(data);
    } catch (e) {
      // Only toast the first time; auto-refresh failures use the inline
      // banner so we don't spam the operator every polling cycle.
      if (!data) toast.error(formatErr(e));
      setErr(e);
    } finally {
      setBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(days); }, [days, load]);

  const rows = data?.rows || [];
  const peakVisitors = rows.reduce((m, r) => Math.max(m, r.unique_visitors || 0), 0);
  const peakSignups = rows.reduce((m, r) => Math.max(m, r.signups || 0), 0);

  return (
    <section
      className="border hairline rounded-2xl p-4 sm:p-5 mb-4 bg-white"
      data-testid="conversion-funnel-card"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="eyebrow flex items-center gap-1.5 text-[#ff4500] mb-1">
            <TrendingUp className="w-3 h-3" /> Growth · conversion funnel
          </div>
          <h2 className="font-display text-lg sm:text-xl tracking-tight leading-tight">
            Anonymous browsers → signups
          </h2>
          <p className="text-[11px] text-[#666] mt-0.5 max-w-2xl">
            Daily views (from the presence log) paired with new accounts created that day.
            Higher rate = the signup prompts are landing.
          </p>
        </div>
        <label className="inline-flex items-center gap-1.5">
          <span className="mono text-[10px] uppercase tracking-wider text-[#666]">Window</span>
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="pl-2 pr-6 py-1.5 text-[11px] mono border hairline bg-white hover:border-[#ff4500] focus:outline-none focus:border-[#ff4500] cursor-pointer"
            data-testid="conversion-funnel-window"
          >
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
          </select>
        </label>
      </div>

      {/* Headline metrics */}
      <div className="grid grid-cols-3 gap-0 border hairline mb-3">
        <div className="p-2.5">
          <div className="mono text-[9px] uppercase tracking-[0.15em] text-[#888] flex items-center gap-1"><Eye className="w-2.5 h-2.5" /> Visitors</div>
          <div className="font-display text-xl tabular-nums leading-none mt-1" data-testid="conversion-total-visitors">
            {num(data?.total_unique_visitors ?? 0)}
          </div>
        </div>
        <div className="p-2.5 border-l hairline">
          <div className="mono text-[9px] uppercase tracking-[0.15em] text-[#888] flex items-center gap-1"><UserPlus className="w-2.5 h-2.5" /> Signups</div>
          <div className="font-display text-xl tabular-nums leading-none mt-1 text-[#0a7f3f]" data-testid="conversion-total-signups">
            {num(data?.total_signups ?? 0)}
          </div>
        </div>
        <div className="p-2.5 border-l hairline">
          <div className="mono text-[9px] uppercase tracking-[0.15em] text-[#888] flex items-center gap-1"><Percent className="w-2.5 h-2.5" /> Rate</div>
          <div className="font-display text-xl tabular-nums leading-none mt-1 text-[#ff4500]" data-testid="conversion-rate">
            {(data?.overall_conversion_rate ?? 0).toFixed(2)}%
          </div>
        </div>
      </div>

      {/* Daily rows */}
      {err && !data ? (
        <AdminErrorState error={err} onRetry={() => load(days)} label="the conversion funnel" />
      ) : busy && !data ? (
        <div className="p-6 text-center"><Loader2 className="w-4 h-4 animate-spin inline" /></div>
      ) : (
        <div className="border hairline overflow-hidden" data-testid="conversion-daily-table">
          <div className="grid grid-cols-[minmax(0,1.4fr)_1fr_1fr_1fr] mono text-[9px] uppercase tracking-wider text-[#888] px-2.5 py-1.5 bg-[#fafafa] border-b hairline">
            <div>Day</div>
            <div className="text-right">Visitors</div>
            <div className="text-right">Signups</div>
            <div className="text-right">Rate</div>
          </div>
          <div className="max-h-72 overflow-y-auto">
            {rows.length === 0 ? (
              <div className="p-3 text-[11px] mono text-[#888] italic">No data yet.</div>
            ) : rows.slice().reverse().map((r) => {
              const vBar = peakVisitors > 0 ? (r.unique_visitors / peakVisitors) * 100 : 0;
              const sBar = peakSignups > 0 ? (r.signups / peakSignups) * 100 : 0;
              return (
                <div
                  key={r.date}
                  className="grid grid-cols-[minmax(0,1.4fr)_1fr_1fr_1fr] items-center px-2.5 py-1.5 text-[11px] mono border-b hairline last:border-b-0 hover:bg-[#fafafa]"
                  data-testid={`conversion-row-${r.date}`}
                >
                  <div className="text-[#333] truncate">{humanDate(r.date)}</div>
                  <div className="relative text-right">
                    <div className="absolute inset-y-0 right-0 bg-[#0e0e0e]/8" style={{ width: `${vBar}%` }} aria-hidden="true" />
                    <span className="relative">{num(r.unique_visitors)}</span>
                  </div>
                  <div className="relative text-right">
                    <div className="absolute inset-y-0 right-0 bg-[#0a7f3f]/15" style={{ width: `${sBar}%` }} aria-hidden="true" />
                    <span className={`relative ${r.signups > 0 ? "text-[#0a7f3f] font-bold" : ""}`}>{num(r.signups)}</span>
                  </div>
                  <div className={`text-right ${r.conversion_rate >= 1 ? "text-[#ff4500] font-bold" : "text-[#888]"}`}>
                    {r.conversion_rate.toFixed(2)}%
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <p className="mt-2 text-[10px] mono text-[#888]">
        UK CPR/DMCC-safe: real counts, no inflation. Signups matched to server-side <code className="text-[10px]">db.users.created_at</code>.
      </p>
    </section>
  );
}
